import { z } from 'zod';
import { parse as parseYaml, parseDocument, stringify as stringifyYaml } from 'yaml';
import type { PromptVersion } from '@/server/db/schema';
import { expandIncludes, type IncludeRef } from '@/lib/promptInterpolation';

/**
 * Prompt files in an agent version's tree (docs/impl/124-sandboxed-agent-versions): `prompts/<type>.md`,
 * YAML frontmatter for the old row's config, markdown body for its text.
 */

export const PROMPTS_DIR = 'prompts/';

export const promptPath = (type: string) => `${PROMPTS_DIR}${type}.md`;

export function promptTypeOfPath(path: string): string | null {
	const match = path.match(/^prompts\/([a-z0-9-]+)\.md$/);
	return match ? match[1]! : null;
}

export function listPromptTypes(files: Record<string, string>): string[] {
	return Object.keys(files)
		.map(promptTypeOfPath)
		.filter((t): t is string => t !== null)
		.sort();
}

/** Meta keys that describe the prompt type rather than one version of it; they live in `manifest.prompts[type]`. */
export const TYPE_META_KEYS = [
	'promptVersionColumnName',
	'promptVersionColumnTitle',
	'sortOrder',
	'primaryAgent',
	'superadminOnly',
	'templateStrings',
	'availableTools',
	'assistantRoles',
] as const;

/**
 * A pi agent tier's `model` and `tools` in `manifest.prompts[type]`. They are read from the manifest
 * directly and never merged into the prompt's meta, where frontmatter `model` and `tools` already live.
 */
export const TIER_MANIFEST_KEYS = new Set(['model', 'tools']);

/** A `manifest.prompts[type]` entry without the pi agent tier keys, ready to merge into a prompt's meta. */
export function promptTypeMeta(entry: Record<string, unknown> | undefined): Record<string, unknown> {
	return Object.fromEntries(Object.entries(entry ?? {}).filter(([key]) => !TIER_MANIFEST_KEYS.has(key)));
}

/** Row draft bookkeeping that the version row now carries; never written to a file. */
const DRAFT_META_KEYS = new Set([
	'draft',
	'shared',
	'sharedOn',
	'baseVersion',
	'draftNumber',
	'expectedLiveVersion',
	'deployedOn',
	'publishedOn',
	'parentDraftId',
	'revisionNumber',
	'linkedDiscussions',
]);

export const promptFrontmatterSchema = z
	.object({
		kind: z.string().default('system'),
		description: z.string().nullish(),
		icon: z.string().nullish(),
		model: z.string().nullish(),
		modelSettings: z.record(z.unknown()).nullish(),
		displayName: z.string().optional(),
		defaultMaxSteps: z.number().int().positive().optional(),
		tools: z.record(z.unknown()).optional(),
		triggers: z.array(z.unknown()).optional(),
		pools: z.record(z.unknown()).optional(),
	})
	.passthrough();

export type PromptFrontmatter = z.infer<typeof promptFrontmatterSchema>;

export interface PromptFile {
	frontmatter: PromptFrontmatter;
	body: string;
}

export class PromptFileError extends Error {}

const FRONTMATTER = /^---\r?\n([\s\S]*?)\r?\n---[ \t]*(\r?\n|$)/;

/** Editor helpers: read the frontmatter loosely, and set single keys without disturbing the rest of the file. */
export interface PromptFileParts {
	frontmatter: string | null;
	body: string;
}

export function splitPromptFile(text: string): PromptFileParts {
	const m = FRONTMATTER.exec(text);
	if (!m) return { frontmatter: null, body: text };
	return { frontmatter: m[1]!, body: text.slice(m[0].length) };
}

export function readFrontmatter(text: string): { data: Record<string, unknown>; error: string | null } {
	const { frontmatter } = splitPromptFile(text);
	if (frontmatter === null) return { data: {}, error: null };
	const doc = parseDocument(frontmatter);
	if (doc.errors.length > 0) return { data: {}, error: doc.errors[0]!.message };
	const data = doc.toJS();
	return { data: data && typeof data === 'object' && !Array.isArray(data) ? data : {}, error: null };
}

/** Sets (or, with `undefined`, removes) one top-level key, keeping the other keys, their order and comments. */
export function setFrontmatterField(text: string, key: string, value: unknown): string {
	const { frontmatter, body } = splitPromptFile(text);
	const doc = parseDocument(frontmatter ?? '');
	if (doc.errors.length > 0) throw new Error(`The frontmatter does not parse: ${doc.errors[0]!.message}`);
	if (value === undefined || value === '') doc.delete(key);
	else doc.set(key, value);
	const yaml = doc.toString().trimEnd();
	const rest = frontmatter === null ? text : body;
	return yaml ? `---\n${yaml}\n---\n${rest}` : rest;
}

/** Replaces the body, keeping the frontmatter exactly as written. */
export function setPromptBody(text: string, body: string): string {
	const { frontmatter } = splitPromptFile(text);
	return frontmatter === null ? body : `---\n${frontmatter}\n---\n${body}`;
}

export function parsePromptFile(text: string): PromptFile {
	const match = text.match(FRONTMATTER);
	if (!match) return { frontmatter: promptFrontmatterSchema.parse({}), body: text };
	let raw: unknown;
	try {
		raw = parseYaml(match[1]!) ?? {};
	} catch (error) {
		throw new PromptFileError(`Frontmatter is not valid YAML: ${error instanceof Error ? error.message : String(error)}`);
	}
	const parsed = promptFrontmatterSchema.safeParse(raw);
	if (!parsed.success) {
		throw new PromptFileError(
			`Frontmatter is invalid: ${parsed.error.issues.map((i) => `${i.path.join('.') || '(root)'} ${i.message}`).join('; ')}`
		);
	}
	return { frontmatter: parsed.data, body: text.slice(match[0].length) };
}

export function serializePromptFile({ frontmatter, body }: PromptFile): string {
	const clean = Object.fromEntries(Object.entries(frontmatter).filter(([, v]) => v !== undefined && v !== null));
	return `---\n${stringifyYaml(clean, { lineWidth: 0 })}---\n${body}`;
}

/** A prompt row as a file, plus the type-level meta that belongs in the manifest. */
export function promptRowToFile(row: PromptVersion): { text: string; typeMeta: Record<string, unknown> } {
	const meta = { ...((row.meta ?? {}) as Record<string, unknown>) };
	const typeMeta: Record<string, unknown> = {};
	for (const key of TYPE_META_KEYS) {
		if (meta[key] !== undefined) typeMeta[key] = meta[key];
		delete meta[key];
	}
	for (const key of DRAFT_META_KEYS) delete meta[key];
	const frontmatter: PromptFrontmatter = {
		...meta,
		kind: row.kind,
		description: row.typeDescription,
		icon: row.typeIcon,
		model: row.model,
		modelSettings: row.modelSettings as Record<string, unknown> | null,
	};
	return { text: serializePromptFile({ frontmatter, body: row.content }), typeMeta };
}

/**
 * The prompt row shape callers already read, built from a file. `base` (normally the type's
 * active row) supplies the identity fields, so ids stay valid foreign keys.
 */
export function promptFromFile(params: {
	agentId: number;
	type: string;
	file: PromptFile;
	typeMeta?: Record<string, unknown>;
	base?: PromptVersion | null;
}): PromptVersion {
	const { kind, description, icon, model, modelSettings, ...meta } = params.file.frontmatter;
	const now = new Date();
	return {
		id: params.base?.id ?? 0,
		agentId: params.agentId,
		createdAt: params.base?.createdAt ?? now,
		updatedAt: params.base?.updatedAt ?? now,
		type: params.type,
		content: params.file.body,
		version: params.base?.version ?? 0,
		versionSlug: params.base?.versionSlug ?? null,
		isActive: params.base?.isActive ?? true,
		description: params.base?.description ?? null,
		versionAuthor: params.base?.versionAuthor ?? null,
		draftState: null,
		model: model ?? null,
		modelSettings: (modelSettings ?? null) as PromptVersion['modelSettings'],
		typeDescription: description ?? null,
		typeIcon: icon ?? null,
		kind,
		meta: { ...meta, ...promptTypeMeta(params.typeMeta) } as PromptVersion['meta'],
	};
}

const COMMENT_LINE = /^[ \t]*<!--[\s\S]*?-->[ \t]*(?:\r?\n|$)/gm;
const COMMENT = /<!--[\s\S]*?-->/g;

/** Drops HTML comments (provenance notes, pinned markers); a line holding only a comment goes entirely. */
export function stripComments(text: string): string {
	return text.replace(COMMENT_LINE, '').replace(COMMENT, '');
}

/**
 * The text the model sees: comments stripped and `{{prompt:type}}` / `{{file:path}}` includes
 * expanded recursively. An unknown or circular include expands to nothing, as row prompts do.
 */
export function renderPromptForInference(
	text: string,
	resolve: (ref: IncludeRef) => string | null,
	visited: Set<string> = new Set()
): string {
	return expandIncludes(stripComments(text), (ref) => {
		const key = `${ref.kind}:${ref.target}`;
		if (visited.has(key)) return '';
		const included = resolve(ref);
		if (included === null) return '';
		return renderPromptForInference(included, resolve, new Set([...visited, key]));
	});
}
